import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import type { App } from "vue";

/**
 * Azure Application Insights — monitoramento de erros e uso no navegador do usuário.
 *
 * Captura automaticamente: erros JS não tratados, page views (rotas), e falhas/latência
 * de chamadas à API (AJAX). Os erros de componentes Vue são reportados via app.config.errorHandler.
 *
 * Se VITE_APPINSIGHTS_CONNECTION_STRING não estiver definida (ex.: dev local), a telemetria
 * é silenciosamente desativada.
 */

let appInsights: ApplicationInsights | null = null;

/**
 * Exceptions que não são bug nosso e só poluem o painel — em 30 dias de produção
 * representaram 27 das 30 exceptions do frontend, escondendo o que importa.
 *
 * - ResizeObserver: ruído conhecido do browser quando um layout reflui dentro do
 *   próprio callback (Vuetify). Não quebra nada e não é acionável.
 * - MetaMask / carteiras cripto: extensão instalada no browser do visitante.
 * - window.webkit.messageHandlers: script injetado por webview de app (Instagram,
 *   Facebook no iOS) ao abrir nossos links públicos.
 * - "Java object is gone": o mesmo caso no Android — a ponte JavaScript da
 *   webview é destruída enquanto a página ainda tenta usá-la. Não chamamos
 *   postMessage em lugar nenhum; é o app hospedeiro, e não há o que corrigir.
 */
const RUIDO_CONHECIDO: RegExp[] = [
  /ResizeObserver loop/i,
  /Failed to connect to MetaMask/i,
  /window\.webkit\.messageHandlers/i,
  /Java object is gone/i,
];

export function ehRuidoConhecido(envelope: {
  baseType?: string;
  baseData?: unknown;
}): boolean {
  if (envelope.baseType !== "ExceptionData") return false;

  const dados = envelope.baseData as
    | { exceptions?: Array<{ message?: string; typeName?: string }> }
    | undefined;

  const textos = (dados?.exceptions ?? []).flatMap((e) => [e.message, e.typeName]);

  return textos.some(
    (texto) => !!texto && RUIDO_CONHECIDO.some((padrao) => padrao.test(texto)),
  );
}

/**
 * Domínios que NÃO recebem os cabeçalhos de correlação (`Request-Id`,
 * `traceparent`) que o SDK injeta no `window.fetch` global.
 *
 * O ViaCEP só declara `Access-Control-Allow-Headers: Content-Type,
 * X-Request-With, X-Requested-By`. Com o cabeçalho de correlação, o GET simples
 * vira requisição com preflight, o preflight é reprovado, o `fetch` estoura e a
 * busca de CEP passa a responder "CEP não encontrado" em toda ficha e em todo
 * cadastro de templo. Em dev nunca aparecia: sem connection string o SDK não
 * carrega e o `fetch` fica o nativo.
 *
 * A chave substitui o default do SDK em vez de somar a ele, por isso a lista
 * de blob storage que já vinha por padrão precisa ser repetida aqui à mão.
 */
export const CORRELATION_HEADER_EXCLUDED_DOMAINS = [
  // Default do SDK (`DfltAjaxCorrelationHeaderExDomains`), repetido inteiro:
  // é o storage de upload de logo e de foto, nas quatro nuvens soberanas.
  "*.blob.core.windows.net",
  "*.blob.core.chinacloudapi.cn",
  "*.blob.core.cloudapi.de",
  "*.blob.core.usgovcloudapi.net",
  "viacep.com.br",
];

function resolveRoleName(): string {
  const env = (import.meta.env.VITE_APP_ENV as string) || "prod";
  const normalized = env.includes("hml") ? "hml" : "prod";
  return `ori-frontend-${normalized}`;
}

export function initAppInsights(app: App): void {
  const connectionString = import.meta.env
    .VITE_APPINSIGHTS_CONNECTION_STRING as string | undefined;

  if (!connectionString) {
    console.warn(
      "[AppInsights] VITE_APPINSIGHTS_CONNECTION_STRING não definida — telemetria desativada.",
    );
    return;
  }

  appInsights = new ApplicationInsights({
    config: {
      connectionString,
      enableAutoRouteTracking: true, // rastreia mudanças de rota como page views
      autoTrackPageVisitTime: true,
      enableUnhandledPromiseRejectionTracking: true,
      disableFetchTracking: false, // rastreia chamadas fetch/XHR (falhas de API)
      enableCorsCorrelation: true,
      correlationHeaderExcludedDomains: CORRELATION_HEADER_EXCLUDED_DOMAINS,
    },
  });

  appInsights.loadAppInsights();

  // Descarta ruído conhecido de browser/extensão antes de enviar (retornar false
  // cancela o envio do item). Registrado primeiro para não gastar processamento
  // com telemetria que seria descartada de qualquer forma.
  appInsights.addTelemetryInitializer((envelope) => {
    if (ehRuidoConhecido(envelope)) return false;
  });

  // Cloud role name: diferencia HML/Prod (mesmo workspace ori-logs)
  const roleName = resolveRoleName();
  appInsights.addTelemetryInitializer((envelope) => {
    envelope.tags = envelope.tags || {};
    envelope.tags["ai.cloud.role"] = roleName;
  });

  appInsights.trackPageView();

  // Reporta erros de componentes Vue ao Application Insights (sem suprimir o comportamento padrão)
  const previousHandler = app.config.errorHandler;
  app.config.errorHandler = (err, instance, info) => {
    appInsights?.trackException({
      exception: err instanceof Error ? err : new Error(String(err)),
      properties: { vueInfo: info },
    });
    if (previousHandler) {
      previousHandler(err, instance, info);
    } else {
      console.error(err);
    }
  };

  console.log(`[AppInsights] Telemetria ativada (role: ${roleName}).`);
}

export function getAppInsights(): ApplicationInsights | null {
  return appInsights;
}
